import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Building2, Users, UsersRound, GraduationCap, Receipt,
  CreditCard, Music, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Sparkles, MessageSquareText, BellRing,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import api from '../../lib/axios';
import { useTenant } from '../../contexts/TenantContext';
import { palette } from '@/lib/mamvPalette';

const fmtNumber = (n) => new Intl.NumberFormat('de-DE').format(n || 0);
const fmtMoney = (n) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);

/**
 * Tiny inline SVG sparkline. values = array of numbers; optional months for tooltip.
 * Renders a polyline + dots; min/max highlighted; height is fixed.
 */
function Sparkline({ values = [], months = [], width = 160, height = 36 }) {
  if (!values || values.length === 0) {
    return <span className="text-xs text-slate-400">—</span>;
  }
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const pad = 3;
  const innerH = height - pad * 2;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = pad + innerH - ((v - min) / range) * innerH;
    return [x, y];
  });
  const pathD = points.map(([x, y], i) => (i === 0 ? `M ${x},${y}` : `L ${x},${y}`)).join(' ');
  const lastVal = values[values.length - 1];
  const lastMonth = months[months.length - 1];
  const isFlat = max === 0;
  return (
    <div className="inline-flex items-center gap-2" title={lastMonth ? `${lastMonth}: ${fmtMoney(lastVal)}` : ''}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
        {!isFlat && (
          <>
            <path d={pathD} fill="none" stroke={palette.primary} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
            {points.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y}
                r={i === points.length - 1 ? 2.5 : 1.2}
                fill={i === points.length - 1 ? palette.primary : palette.neutral} />
            ))}
          </>
        )}
        {isFlat && (
          <line x1="0" y1={height / 2} x2={width} y2={height / 2}
            stroke="#cbd5e1" strokeWidth="1" strokeDasharray="2,3" />
        )}
      </svg>
      <span className="text-[11px] text-slate-500 tabular-nums whitespace-nowrap">
        {isFlat ? '—' : fmtMoney(lastVal)}
      </span>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sublabel, accent, testid }) {
  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</p>
            <p className={`text-2xl font-bold mt-2 ${accent || 'text-slate-900'}`}>{value}</p>
            {sublabel && <p className="text-xs text-slate-500 mt-1">{sublabel}</p>}
          </div>
          {Icon && (
            <div className="rounded-lg bg-brand-red/5 p-2">
              <Icon className="w-5 h-5 text-brand-red" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SmsUsageCard({ usage }) {
  if (!usage || !usage.tenants?.length) return null;
  const barColor = (pct) => (pct >= 90 ? 'bg-rose-500' : pct >= 60 ? 'bg-brand-warning' : 'bg-emerald-500');
  const txtColor = (pct) => (pct >= 90 ? 'text-rose-700' : pct >= 60 ? 'text-brand-warning' : 'text-emerald-700');
  return (
    <Card className="border-slate-200" data-testid="sms-usage-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquareText className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-slate-800">SMS-Verifikations-Kontingent (letzte 24 h)</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Bestätigungscodes der Online-Anmeldung · Limit {usage.limit} pro Mandant und Tag
        </p>
        <div className="space-y-3">
          {usage.tenants.map((t) => {
            const pct = Math.min(100, Math.round((t.verification_codes_24h / (t.limit || 1)) * 100));
            return (
              <div key={t.tenant_id} data-testid={`sms-usage-row-${t.slug}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-800">{t.name}</span>
                  <span className={`tabular-nums text-xs font-medium ${txtColor(pct)}`} data-testid={`sms-usage-count-${t.slug}`}>
                    {t.verification_codes_24h} / {t.limit}
                    {pct >= 90 && <span className="ml-1">· Kontingent fast erreicht</span>}
                  </span>
                </div>
                <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${barColor(pct)} transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-0.5 text-[11px] text-slate-400">
                  {t.remaining} Codes verbleibend · {t.sms_total_24h} SMS gesamt (alle Arten, 24 h)
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function AlertHistoryCard({ alerts }) {
  const navigate = useNavigate();
  if (!alerts?.length) return null;
  const fmtTime = (iso) => new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <Card className="border-slate-200" data-testid="admin-alerts-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <BellRing className="w-4 h-4 text-rose-500" strokeWidth={1.5} />
          <h3 className="text-sm font-semibold text-slate-800">Warnungen</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Letzte Kontingent- und Fehler-Warnungen (auch falls die Push-Nachricht verpasst wurde)</p>
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {alerts.map((a) => (
            <button
              key={a.id}
              onClick={() => a.url && navigate(a.url)}
              className="w-full text-left flex items-start gap-2 rounded-md border border-slate-100 hover:bg-slate-50 px-3 py-2 transition-colors"
              data-testid={`admin-alert-${a.id}`}
            >
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${a.alert_type === 'sms_quota' ? 'bg-brand-red' : 'bg-rose-500'}`} />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">{a.title}</span>
                <span className="block text-xs text-slate-500 truncate">{a.body}</span>
                <span className="block text-[11px] text-slate-400 mt-0.5">{fmtTime(a.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function MultiTenantOverview() {
  const { isSuperAdmin } = useTenant();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [smsUsage, setSmsUsage] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resOv, resTr, resSms, resAl] = await Promise.all([
        api.get('/overview/multi-tenant'),
        api.get('/overview/monthly-trends?months=12'),
        api.get('/overview/sms-usage'),
        api.get('/overview/admin-alerts').catch(() => ({ data: { alerts: [] } })),
      ]);
      setData(resOv.data);
      setTrends(resTr.data);
      setSmsUsage(resSms.data);
      setAlerts(resAl.data.alerts || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Übersicht konnte nicht geladen werden.';
      setError(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto p-6" data-testid="overview-no-permission">
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-10 h-10 text-brand-warning mx-auto mb-3" strokeWidth={1.5} />
              <h2 className="text-xl font-semibold text-slate-900">Kein Zugriff</h2>
              <p className="text-sm text-slate-600 mt-2">
                Diese mandantenübergreifende Übersicht ist nur für Super-Admins zugänglich.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const totals = data?.totals;
  const rows = data?.tenants || [];

  // Compute biggest month-over-month movers (€ and %) from the trend series.
  // Compares the last month bucket vs the previous month bucket.
  const movers = (() => {
    if (!trends?.series || trends.series.length === 0) return null;
    const months = trends.months || [];
    if (months.length < 2) return null;
    const lastIdx = months.length - 1;
    const prevIdx = lastIdx - 1;
    let topAbs = null;  // largest absolute € change (signed)
    let topPct = null;  // largest % change where prev > 0
    for (const s of trends.series) {
      const last = s.values?.[lastIdx] ?? 0;
      const prev = s.values?.[prevIdx] ?? 0;
      const delta = last - prev;
      if (delta !== 0 && (topAbs === null || Math.abs(delta) > Math.abs(topAbs.delta))) {
        topAbs = { ...s, last, prev, delta };
      }
      if (prev > 0) {
        const pct = (delta / prev) * 100;
        if (topPct === null || Math.abs(pct) > Math.abs(topPct.pct)) {
          topPct = { ...s, last, prev, delta, pct };
        }
      }
    }
    if (!topAbs && !topPct) return null;
    return { topAbs, topPct, lastMonth: months[lastIdx], prevMonth: months[prevIdx] };
  })();

  const goToGuardians = (tenantId, status) => {
    // switchTenant() triggers window.location.reload() which kills navigate().
    // We replicate its behavior, but assign a target URL directly so the reload lands
    // on the filtered Guardians page.
    localStorage.setItem('active_tenant_id', tenantId);
    window.location.href = `/guardians?mandate_status=${status}`;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="multi-tenant-overview-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Mandantenübergreifende Übersicht
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              KPIs aller aktiven Mandanten · Stand {data?.as_of || '—'}
            </p>
          </div>
          <Button onClick={load} variant="outline" disabled={loading} data-testid="overview-reload-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Neu laden
          </Button>
        </div>

        {error && (
          <Card className="border-rose-200 bg-rose-50">
            <CardContent className="p-4 text-sm text-rose-800">{error}</CardContent>
          </Card>
        )}

        {/* Summen-Karten */}
        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="overview-totals">
            <KpiCard testid="kpi-tenant-count" icon={Building2}
              label="Mandanten" value={fmtNumber(data.tenant_count)} />
            <KpiCard testid="kpi-students" icon={Users}
              label="Schüler aktiv" value={fmtNumber(totals.students_active)} />
            <KpiCard testid="kpi-families" icon={UsersRound}
              label="Familien" value={fmtNumber(totals.families)} />
            <KpiCard testid="kpi-mrr" icon={TrendingUp}
              label="MRR (geschätzt)" value={fmtMoney(totals.monthly_recurring)}
              sublabel="Σ aktive Belegungen × Tarif" accent="text-emerald-700" />
            <KpiCard testid="kpi-invoices-open" icon={Receipt}
              label="Offene Rechnungen"
              value={fmtMoney(totals.invoices_open_amount)}
              sublabel={`${fmtNumber(totals.invoices_open_count)} Rechnungen`}
              accent="text-amber-700" />
            <KpiCard testid="kpi-rentals" icon={Music}
              label="Aktive Mietverträge"
              value={fmtNumber(totals.rentals_active)}
              sublabel={`${fmtMoney(totals.rentals_monthly_sum)} / Monat`} />
          </div>
        )}

        {/* Top-Bewegung */}
        {movers && (movers.topAbs || movers.topPct) && (
          <Card className="border-brand-red/20 bg-gradient-to-r from-brand-red/5 to-slate-50" data-testid="top-mover-card">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-white/70 p-2 shrink-0">
                  <Sparkles className="w-5 h-5 text-brand-red" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wide text-brand-red font-semibold">
                    Top-Bewegung · {movers.prevMonth} → {movers.lastMonth}
                  </p>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {movers.topAbs && (
                      <div data-testid="top-mover-abs" className="text-sm">
                        <span className="text-slate-600">Größter €-Sprung:</span>{' '}
                        <span className="font-semibold text-slate-900">{movers.topAbs.name}</span>{' '}
                        <span className="text-xs text-slate-500">({movers.topAbs.slug})</span>
                        <div className="mt-1 flex items-center gap-2">
                          {movers.topAbs.delta >= 0
                            ? <TrendingUp className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                            : <TrendingDown className="w-4 h-4 text-rose-600" strokeWidth={2} />}
                          <span className={`tabular-nums font-medium ${movers.topAbs.delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {movers.topAbs.delta >= 0 ? '+' : ''}{fmtMoney(movers.topAbs.delta)}
                          </span>
                          <span className="text-xs text-slate-500">
                            ({fmtMoney(movers.topAbs.prev)} → {fmtMoney(movers.topAbs.last)})
                          </span>
                        </div>
                      </div>
                    )}
                    {movers.topPct && (
                      <div data-testid="top-mover-pct" className="text-sm">
                        <span className="text-slate-600">Größte %-Veränderung:</span>{' '}
                        <span className="font-semibold text-slate-900">{movers.topPct.name}</span>{' '}
                        <span className="text-xs text-slate-500">({movers.topPct.slug})</span>
                        <div className="mt-1 flex items-center gap-2">
                          {movers.topPct.pct >= 0
                            ? <TrendingUp className="w-4 h-4 text-emerald-600" strokeWidth={2} />
                            : <TrendingDown className="w-4 h-4 text-rose-600" strokeWidth={2} />}
                          <span className={`tabular-nums font-medium ${movers.topPct.pct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {movers.topPct.pct >= 0 ? '+' : ''}{movers.topPct.pct.toFixed(1)} %
                          </span>
                          <span className="text-xs text-slate-500">
                            ({fmtMoney(movers.topPct.prev)} → {fmtMoney(movers.topPct.last)})
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* SMS-Kontingent */}
        <AlertHistoryCard alerts={alerts} />
        <SmsUsageCard usage={smsUsage} />

        {/* Per-Mandant-Tabelle */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="overview-tenant-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-slate-600">
                  <th className="px-4 py-3 font-semibold">Mandant</th>
                  <th className="px-4 py-3 font-semibold text-right">Schüler</th>
                  <th className="px-4 py-3 font-semibold text-right">Familien</th>
                  <th className="px-4 py-3 font-semibold text-right">Kurse</th>
                  <th className="px-4 py-3 font-semibold text-right">Belegungen</th>
                  <th className="px-4 py-3 font-semibold text-right">MRR</th>
                  <th className="px-4 py-3 font-semibold text-right">Offen (€)</th>
                  <th className="px-4 py-3 font-semibold text-right">SEPA-Läufe</th>
                  <th className="px-4 py-3 font-semibold text-right">Inst. / Vermietet</th>
                  <th className="px-4 py-3 font-semibold">Mandate</th>
                  <th className="px-4 py-3 font-semibold">Trend Rg. (12 Mo)</th>
                  <th className="px-4 py-3 font-semibold">Rabattmodell</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-slate-500">
                      Lade Daten …
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="px-4 py-8 text-center text-slate-500">
                      Keine Mandanten gefunden.
                    </td>
                  </tr>
                )}
                {rows.map((t) => {
                  const scheme = t.discount_scheme;
                  const schemeLabel = !scheme
                    ? 'Standard (10/20/30 %)'
                    : scheme.type === 'count_uniform'
                    ? `Count-Uniform (Fallback ${Math.round((scheme.fallback || 0) * 100)} %)`
                    : scheme.type === 'position_tiers'
                    ? `Position-Tiers (${(scheme.tiers || []).map((x) => `${Math.round(x * 100)}%`).join('/')})`
                    : 'benutzerdefiniert';
                  const trendSeries = trends?.series?.find((s) => s.tenant_id === t.tenant_id);
                  return (
                    <tr key={t.tenant_id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`overview-row-${t.slug}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{t.name}</div>
                        <div className="text-xs text-slate-500">{t.slug}</div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(t.students_active)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(t.families)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(t.courses)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(t.enrollments_active)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                        {fmtMoney(t.monthly_recurring)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className={t.invoices_open_count > 0 ? 'text-amber-700 font-medium' : 'text-slate-400'}>
                          {fmtMoney(t.invoices_open_amount)}
                        </span>
                        <div className="text-[11px] text-slate-500">
                          {fmtNumber(t.invoices_open_count)} Rg.
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <div>{fmtNumber(t.sepa_runs_total)}</div>
                        {t.sepa_runs_unexported > 0 && (
                          <div className="text-[11px] text-brand-red">
                            {fmtNumber(t.sepa_runs_unexported)} offen
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <div>{fmtNumber(t.instruments_total)}</div>
                        <div className="text-[11px] text-slate-500">
                          {fmtNumber(t.instruments_rented)} vermietet
                        </div>
                      </td>
                      <td className="px-4 py-3" data-testid={`overview-mandates-${t.slug}`}>
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => goToGuardians(t.tenant_id, 'active')}
                            className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition cursor-pointer"
                            title="Aktiv (in den letzten 36 Monaten genutzt) — Klick filtert Guardians"
                            data-testid={`mandate-pill-active-${t.slug}`}
                          >
                            🟢 {fmtNumber(t.mandates_active)}
                          </button>
                          <button
                            onClick={() => goToGuardians(t.tenant_id, 'issued')}
                            className="text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 transition cursor-pointer"
                            title="Erteilt — Erstlastschrift erwartet"
                            data-testid={`mandate-pill-issued-${t.slug}`}
                          >
                            🔵 {fmtNumber(t.mandates_issued)}
                          </button>
                          {t.mandates_expired > 0 && (
                            <button
                              onClick={() => goToGuardians(t.tenant_id, 'expired')}
                              className="text-[11px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 font-semibold hover:bg-rose-100 transition cursor-pointer"
                              title="Erloschen >36 Monate — Klick filtert Guardians, dann Brief-CSV exportieren"
                              data-testid={`mandate-pill-expired-${t.slug}`}
                            >
                              🔴 {fmtNumber(t.mandates_expired)}
                            </button>
                          )}
                          {t.mandates_no_mandate > 0 && (
                            <button
                              onClick={() => goToGuardians(t.tenant_id, 'no_mandate')}
                              className="text-[11px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100 transition cursor-pointer"
                              title="Kein Mandat hinterlegt — Klick filtert Guardians"
                              data-testid={`mandate-pill-no-mandate-${t.slug}`}
                            >
                              ⚫ {fmtNumber(t.mandates_no_mandate)}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-testid={`overview-trend-${t.slug}`}>
                        <Sparkline values={trendSeries?.values || []} months={trends?.months || []} />
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-[11px] font-normal">
                          {schemeLabel}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr className="font-semibold text-slate-800" data-testid="overview-totals-row">
                    <td className="px-4 py-3">Σ {fmtNumber(data.tenant_count)} Mandanten</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(totals.students_active)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(totals.families)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(totals.courses)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(totals.enrollments_active)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-emerald-700">
                      {fmtMoney(totals.monthly_recurring)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-700">
                      {fmtMoney(totals.invoices_open_amount)}
                      <div className="text-[11px] font-normal text-slate-500">
                        {fmtNumber(totals.invoices_open_count)} Rg.
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmtNumber(totals.sepa_runs_total)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {fmtNumber(totals.instruments_total)}
                      <div className="text-[11px] font-normal text-slate-500">
                        {fmtNumber(totals.instruments_rented)} vermietet
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                          🟢 {fmtNumber(totals.mandates_active)}
                        </span>
                        {totals.mandates_expired > 0 && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 font-semibold">
                            🔴 {fmtNumber(totals.mandates_expired)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">letzte 12 Mo</td>
                    <td className="px-4 py-3 text-xs text-slate-500">—</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>

        <p className="text-xs text-slate-400 text-center">
          <CreditCard className="inline w-3 h-3 mr-1" />
          MRR = Σ aktive Belegungen × Tarif (monatlich) ohne Rabatte/GEMA. Werte sind Schätzwerte.
          Für genauen Umsatz siehe Rechnungslauf-Vorschau pro Mandant. <GraduationCap className="inline w-3 h-3 ml-2" />
        </p>
      </div>
    </Layout>
  );
}
